// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Switch} from '@app/features/ui/components/form/Switch';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/PremiumBadgeSettings.module.css';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

interface PremiumBadgeSettingsProps {
	premiumBadgeHidden: boolean;
	premiumBadgeTimestampHidden: boolean;
	premiumBadgeMasked: boolean;
	premiumBadgeSequenceHidden: boolean;
	disabled?: boolean;
	onToggle: (
		field:
			| 'premium_badge_hidden'
			| 'premium_badge_timestamp_hidden'
			| 'premium_badge_masked'
			| 'premium_badge_sequence_hidden',
		value: boolean,
	) => void;
	hasLifetimePremium: boolean;
	premiumSince?: Date | null;
	premiumLifetimeSequence?: number | null;
}
export const PremiumBadgeSettings = observer(
	({
		premiumBadgeHidden,
		premiumBadgeTimestampHidden,
		premiumBadgeMasked,
		premiumBadgeSequenceHidden,
		disabled,
		onToggle,
		hasLifetimePremium,
		premiumSince,
		premiumLifetimeSequence,
	}: PremiumBadgeSettingsProps) => {
		const {i18n} = useLingui();
		return (
			<div>
				<div className={styles.header}>
					<h2 className={styles.title}>
						<Trans>{PREMIUM_PRODUCT_NAME} Badge Privacy</Trans>
					</h2>
					<p className={styles.description}>
						<Trans>Control how your {PREMIUM_PRODUCT_NAME} badge is displayed to others</Trans>
					</p>
				</div>
				<div className={styles.switches}>
					<Switch
						label={i18n._(msg`Hide ${PREMIUM_PRODUCT_NAME} badge entirely`)}
						description={i18n._(msg`Completely hide your ${PREMIUM_PRODUCT_NAME} badge from other users`)}
						value={premiumBadgeHidden}
						onChange={(value) => onToggle('premium_badge_hidden', value)}
						disabled={disabled}
					/>
					<Switch
						label={
							premiumSince
								? i18n._(
										msg`Hide ${PREMIUM_PRODUCT_NAME} purchase date (${DateUtils.getFormattedShortDate(premiumSince)})`,
									)
								: i18n._(msg`Hide ${PREMIUM_PRODUCT_NAME} purchase date`)
						}
						description={i18n._(msg`Remove when you first bought ${PREMIUM_PRODUCT_NAME} from your badge`)}
						value={premiumBadgeTimestampHidden}
						onChange={(value) => onToggle('premium_badge_timestamp_hidden', value)}
						disabled={disabled || premiumBadgeHidden}
					/>
					{hasLifetimePremium && (
						<Switch
							label={i18n._(msg`Mask Visionary as subscription`)}
							description={i18n._(msg`Show your Visionary as a regular subscription instead`)}
							value={premiumBadgeMasked}
							onChange={(value) => onToggle('premium_badge_masked', value)}
							disabled={disabled || premiumBadgeHidden}
						/>
					)}
					{hasLifetimePremium && (
						<Switch
							label={
								premiumLifetimeSequence != null
									? i18n._(msg`Hide Visionary ID badge (#${premiumLifetimeSequence})`)
									: i18n._(msg`Hide Visionary ID badge`)
							}
							description={i18n._(msg`Remove your Visionary ID badge`)}
							value={premiumBadgeSequenceHidden}
							onChange={(value) => onToggle('premium_badge_sequence_hidden', value)}
							disabled={disabled || premiumBadgeHidden || premiumBadgeMasked}
						/>
					)}
				</div>
			</div>
		);
	},
);
